import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, History, SlidersHorizontal, MonitorSmartphone, Target } from "lucide-react";
import Globe from "@/components/Globe";
import Marquee from "@/components/Marquee";
import CTASection from "@/components/CTASection";
import { Reveal, MaskLines, FadeIn } from "@/components/Reveal";
import { useSEO } from "@/lib/seo";

const IMG_DESK =
    "https://images.unsplash.com/photo-1744051513107-1d3b25e7ee1f?crop=entropy&cs=srgb&fm=jpg&q=85&auto=format&fit=crop&w=1200";
const IMG_TERRITORIO =
    "https://images.unsplash.com/photo-1789995377270-f5f3d312df7f?auto=format&fit=crop&w=1800&q=80";

const SERVICES = [
    {
        n: "01",
        ateco: "ATECO 63.10.21",
        title: "Elaborazione dati contabili",
        text: "Servizi di elaborazione e gestione dei dati contabili a supporto delle attività e dei professionisti.",
        testid: "service-card-contabile",
    },
    {
        n: "02",
        ateco: "ATECO 82.10.00",
        title: "Attività amministrativa e di supporto",
        text: "Supporto alle attività amministrative e organizzative per aiutare le imprese a gestire con maggiore efficienza i processi operativi.",
        testid: "service-card-amministrativo",
    },
    {
        n: "03",
        ateco: "ATECO 70.20.09",
        title: "Consulenza imprenditoriale e gestionale",
        text: "Consulenza e supporto alla gestione dell'impresa, con un approccio orientato alle esigenze concrete e agli obiettivi dell'attività.",
        testid: "service-card-consulenza",
    },
];

const STEPS = [
    { n: "01", t: "Ci contatti" },
    { n: "02", t: "Analizziamo la tua esigenza" },
    { n: "03", t: "Gestiamo documenti e attività online" },
    { n: "04", t: "Ti aggiorniamo durante il processo" },
];

const VALUES = [
    { icon: History, t: "ESPERIENZA", d: "Oltre 30 anni di attività." },
    { icon: SlidersHorizontal, t: "FLESSIBILITÀ", d: "Servizi pensati per adattarsi alle esigenze dell'impresa." },
    { icon: MonitorSmartphone, t: "DIGITALIZZAZIONE", d: "Gestione semplice e possibilità di lavorare da remoto." },
    { icon: Target, t: "CONCRETEZZA", d: "Un approccio orientato alle esigenze reali dell'attività." },
];

export default function Home() {
    useSEO(
        "Service & Business SRLS | Servizi contabili, amministrativi e consulenza",
        "Service & Business SRLS: consulenza fiscale e gestionale, servizi contabili e amministrativi, bandi e finanza agevolata. Oltre 30 anni di esperienza, operatività anche da remoto."
    );

    const terrRef = useRef(null);
    const { scrollYProgress } = useScroll({ target: terrRef, offset: ["start end", "end start"] });
    const imgY = useTransform(scrollYProgress, [0, 1], ["-7%", "7%"]);

    return (
        <>
            {/* HERO */}
            <section className="relative overflow-hidden">
                <div
                    className="absolute inset-0 pointer-events-none"
                    style={{ background: "radial-gradient(55% 45% at 72% 30%, rgba(10,95,194,0.25) 0%, rgba(11,15,23,0) 70%)" }}
                />
                <div className="wrap relative grid lg:grid-cols-[1.05fr_0.95fr] gap-14 items-center pt-36 pb-16 lg:pt-40 lg:pb-24 lg:min-h-[88vh]">
                    <div>
                        <FadeIn delay={0.05}>
                            <p className="eyebrow mb-7">Service &amp; Business SRLS — Isernia, Italia</p>
                        </FadeIn>
                        <h1 className="font-display font-bold tracking-[-0.02em] leading-[1.05] text-[clamp(2.3rem,4.4vw,4rem)] text-white">
                            <MaskLines
                                lines={["Supportiamo", "le imprese", "nella gestione", "e nello sviluppo."]}
                                gradientLast
                                delay={0.15}
                            />
                        </h1>
                        <FadeIn delay={0.6}>
                            <p className="mt-7 max-w-xl lead">
                                Servizi contabili, amministrativi e consulenza imprenditoriale. Con l'esperienza di oltre
                                30 anni e la flessibilità di una nuova realtà.
                            </p>
                        </FadeIn>
                        <FadeIn delay={0.75}>
                            <div className="mt-10 flex flex-wrap items-center gap-4">
                                <Link to="/servizi" data-testid="hero-cta-servizi" className="btn-primary">
                                    Scopri i nostri servizi
                                    <ArrowRight className="h-4 w-4" />
                                </Link>
                                <Link to="/contatti" data-testid="hero-cta-contatti" className="btn-ghost">
                                    Parla con noi
                                </Link>
                            </div>
                        </FadeIn>
                    </div>
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 1.1, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
                    >
                        <Globe />
                    </motion.div>
                </div>

                <div className="border-t border-white/8">
                    <div className="wrap py-6 flex flex-wrap gap-x-12 gap-y-3">
                        {["30+ ANNI DI ESPERIENZA", "SEDE: ISERNIA (MOLISE)", "OPERATIVI ANCHE DA REMOTO"].map((s) => (
                            <p key={s} className="font-mono text-[10px] tracking-[0.3em] text-white/40">
                                {s}
                            </p>
                        ))}
                    </div>
                </div>
            </section>

            <Marquee />

            {/* 30 ANNI — sezione chiara */}
            <section className="section-light bg-paper text-slate-900 py-24 sm:py-28">
                <div className="wrap grid lg:grid-cols-2 gap-14 items-center">
                    <Reveal>
                        <div className="relative overflow-hidden rounded-3xl">
                            <img
                                src={IMG_DESK}
                                alt="Scrivania con documenti e strumenti di lavoro, dettaglio dell'attività quotidiana"
                                className="w-full h-64 sm:h-80 object-cover"
                                loading="lazy"
                            />
                            <div className="mt-10 flex items-end gap-5">
                                <p
                                    className="font-display font-extrabold leading-none tracking-tight text-[clamp(4.5rem,9vw,8rem)]"
                                    style={{ color: "transparent", backgroundImage: "linear-gradient(120deg,#2FB8EC,#0A5FC2)", WebkitBackgroundClip: "text", backgroundClip: "text" }}
                                >
                                    30+
                                </p>
                                <p className="font-mono text-[11px] tracking-[0.3em] text-slate-600 uppercase pb-4">
                                    Anni di<br />esperienza
                                </p>
                            </div>
                        </div>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <p className="eyebrow mb-6">La nostra storia</p>
                        <h2 className="h2">L'esperienza non si improvvisa.</h2>
                        <p className="mt-6 lead">
                            Service &amp; Business nasce da un'esperienza maturata in oltre 30 anni di attività al fianco
                            di imprese e professionisti. Oggi questa esperienza vive in una nuova realtà societaria, con
                            un approccio più flessibile, servizi digitali e la possibilità di lavorare anche
                            completamente da remoto.
                        </p>
                        <div className="mt-9">
                            <Link to="/chi-siamo" data-testid="home-cta-chi-siamo" className="btn-ghost !text-slate-900 !border-slate-900/25 hover:!bg-slate-900/5">
                                Scopri chi siamo
                                <ArrowRight className="h-4 w-4" />
                            </Link>
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* SERVIZI */}
            <section className="py-24 sm:py-28" id="servizi">
                <div className="wrap">
                    <Reveal>
                        <p className="eyebrow mb-6">Servizi</p>
                        <h2 className="h2 max-w-3xl">Un unico punto di riferimento per la gestione della tua attività.</h2>
                        <p className="mt-6 max-w-3xl lead">
                            Dalla gestione dei dati contabili al supporto amministrativo, fino alla consulenza
                            imprenditoriale e gestionale: Service &amp; Business affianca imprese e professionisti nelle
                            attività che richiedono competenza, organizzazione e continuità.
                        </p>
                    </Reveal>
                    <div className="mt-14 grid md:grid-cols-3 gap-6">
                        {SERVICES.map((s, i) => (
                            <Reveal key={s.n} delay={i * 0.08} className="h-full">
                                <article
                                    data-testid={s.testid}
                                    className="card-dark rounded-2xl p-8 h-full flex flex-col transition-all duration-300 hover:-translate-y-1.5 hover:border-sky-400/40 group"
                                >
                                    <div className="flex items-start justify-between">
                                        <p className="font-mono text-[10px] tracking-[0.22em] text-sky-400 mt-2">{s.ateco}</p>
                                        <p className="font-display font-extrabold text-5xl text-white/8 leading-none">{s.n}</p>
                                    </div>
                                    <h3 className="font-display text-xl font-semibold text-white mt-8 leading-snug">{s.title}</h3>
                                    <p className="text-sm text-slate-400 leading-relaxed mt-4 flex-1">{s.text}</p>
                                    <Link
                                        to="/servizi"
                                        data-testid={`${s.testid}-cta`}
                                        className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-sky-400 transition-colors hover:text-sky-300"
                                    >
                                        Scopri il servizio
                                        <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                                    </Link>
                                </article>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* FINANZA AGEVOLATA */}
            <section className="py-8 sm:py-12">
                <div className="wrap">
                    <Reveal>
                        <div className="relative rounded-[1.7rem] p-px" style={{ background: "linear-gradient(135deg, rgba(65,198,241,0.65), rgba(10,95,194,0.35) 50%, rgba(65,198,241,0.65))" }}>
                            <div className="relative overflow-hidden rounded-[1.65rem] bg-[#0C1320] px-8 py-14 sm:px-14 sm:py-16">
                                <div
                                    className="absolute -top-32 -right-20 h-80 w-80 rounded-full pointer-events-none"
                                    style={{ background: "radial-gradient(closest-side, rgba(0,210,255,0.18), rgba(0,0,0,0))" }}
                                />
                                <div
                                    className="absolute -bottom-32 -left-20 h-80 w-80 rounded-full pointer-events-none"
                                    style={{ background: "radial-gradient(closest-side, rgba(10,95,194,0.22), rgba(0,0,0,0))" }}
                                />
                                <div className="relative max-w-2xl">
                                    <p className="font-mono text-[10px] tracking-[0.3em] text-sky-400 uppercase">Bandi &amp; Finanza agevolata</p>
                                    <h2 className="h2 mt-5">La tua prossima opportunità potrebbe essere già disponibile.</h2>
                                    <p className="mt-5 text-slate-300 font-medium">
                                        Bandi, contributi e finanza agevolata possono rappresentare una risorsa importante
                                        per lo sviluppo di un'impresa.
                                    </p>
                                    <p className="mt-4 text-sm text-slate-400 leading-relaxed">
                                        Service &amp; Business affianca imprese e professionisti nell'individuazione e nella
                                        gestione delle opportunità di finanza agevolata, aiutandoli a orientarsi tra
                                        bandi, agevolazioni e strumenti disponibili.
                                    </p>
                                    <div className="mt-9">
                                        <Link to="/bandi-finanza-agevolata" data-testid="home-cta-bandi" className="btn-primary">
                                            Scopri bandi e finanza agevolata
                                            <ArrowRight className="h-4 w-4" />
                                        </Link>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* FULL REMOTE */}
            <section className="py-24 sm:py-28">
                <div className="wrap grid lg:grid-cols-2 gap-14">
                    <Reveal>
                        <p className="eyebrow mb-6">Full Remote</p>
                        <h2 className="h2">La distanza non è un limite.</h2>
                        <p className="mt-6 lead">
                            Puoi affidarti a Service &amp; Business anche se non ti trovi a Isernia. La gestione digitale
                            delle attività ci permette di seguire pratiche e servizi anche completamente da remoto,
                            offrendo un rapporto professionale semplice, organizzato e accessibile ovunque ti trovi.
                        </p>
                        <div className="mt-9">
                            <Link to="/full-remote" data-testid="home-cta-remote" className="btn-ghost">
                                Scopri come funziona
                                <ArrowRight className="h-4 w-4" />
                            </Link>
                        </div>
                    </Reveal>
                    <div>
                        {STEPS.map((s, i) => (
                            <Reveal key={s.n} delay={i * 0.06}>
                                <div className="flex items-center gap-6 py-6 border-t border-white/10 group" data-testid={`remote-step-${i + 1}`}>
                                    <span className="font-mono text-sm text-sky-400">{s.n}</span>
                                    <span className="font-display text-xl sm:text-2xl font-semibold text-white/85 group-hover:text-white group-hover:translate-x-1.5 transition-all duration-300">
                                        {s.t}
                                    </span>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* TERRITORIO */}
            <section ref={terrRef} className="relative overflow-hidden" data-testid="territorio-section">
                <motion.img
                    style={{ y: imgY }}
                    src={IMG_TERRITORIO}
                    alt="Vista aerea di un borgo italiano su collina, simbolo del radicamento nel territorio"
                    className="absolute -top-[9%] left-0 h-[118%] w-full object-cover"
                    loading="lazy"
                />
                <div className="absolute inset-0 bg-ink/75" />
                <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, #0B0F17 0%, rgba(11,15,23,0.2) 30%, rgba(11,15,23,0.2) 70%, #0B0F17 100%)" }} />
                <div className="wrap relative py-28 sm:py-36">
                    <Reveal className="max-w-2xl">
                        <p className="eyebrow mb-6">Territorio</p>
                        <h2 className="h2">Radicati nel territorio. Operativi oltre il territorio.</h2>
                        <p className="mt-6 lead">
                            Service &amp; Business ha sede a Isernia e nasce da una lunga esperienza maturata nel
                            territorio. Grazie agli strumenti digitali, oggi possiamo seguire clienti anche al di fuori
                            della nostra area geografica.
                        </p>
                    </Reveal>
                </div>
            </section>

            {/* APPROCCIO */}
            <section className="py-24 sm:py-28">
                <div className="wrap">
                    <Reveal>
                        <p className="eyebrow mb-6">Il nostro approccio</p>
                        <h2 className="h2 max-w-3xl">Più di un servizio. Un supporto concreto alla tua attività.</h2>
                    </Reveal>
                    <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                        {VALUES.map((v, i) => (
                            <Reveal key={v.t} delay={i * 0.07} className="h-full">
                                <div className="card-dark rounded-2xl p-7 h-full transition-all duration-300 hover:border-sky-400/40">
                                    <span className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-sky-400/30 bg-sky-400/5">
                                        <v.icon className="h-5 w-5 text-sky-400" />
                                    </span>
                                    <h3 className="font-display text-base font-bold tracking-[0.12em] text-white mt-6">{v.t}</h3>
                                    <p className="text-sm text-slate-400 leading-relaxed mt-3">{v.d}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* CHI SIAMO PREVIEW */}
            <section className="py-24 sm:py-28 border-t border-white/8">
                <div className="wrap grid lg:grid-cols-2 gap-14">
                    <Reveal>
                        <p className="eyebrow mb-6">Chi siamo</p>
                        <h2 className="h2">Una nuova società, costruita su un'esperienza di oltre 30 anni.</h2>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <p className="lead">
                            Service &amp; Business SRLS nasce con l'obiettivo di mettere a disposizione di imprese e
                            professionisti un insieme di servizi contabili, amministrativi e consulenziali organizzati
                            secondo un approccio moderno e flessibile.
                        </p>
                        <p className="mt-5 text-base text-slate-400 leading-relaxed">
                            L'esperienza maturata in oltre 30 anni rappresenta il punto di partenza. La nuova società
                            guarda invece alle esigenze delle imprese di oggi: maggiore flessibilità, strumenti digitali,
                            gestione da remoto e un rapporto più agile con il cliente.
                        </p>
                        <div className="mt-9">
                            <Link to="/chi-siamo" data-testid="home-chi-siamo-link" className="btn-ghost">
                                Scopri chi siamo
                                <ArrowRight className="h-4 w-4" />
                            </Link>
                        </div>
                    </Reveal>
                </div>
            </section>

            <CTASection
                title="Parliamo della tua attività."
                text="Hai bisogno di supporto contabile, amministrativo, gestionale o vuoi capire quali opportunità possono essere disponibili per la tua impresa? Raccontaci la tua esigenza."
                testid="home-final-cta"
            />
        </>
    );
}
